import { useState, useEffect, useRef } from 'react';
import { BuildingOffice2Icon, ChevronUpDownIcon } from '@heroicons/react/24/outline';
import { useAuthStore } from '@/store/auth';
import { authService } from '@/services';
import toast from 'react-hot-toast';
import type { BrokerOption, BrokerContext } from '@/types';

export function BrokerSwitcher() {
  const [brokers, setBrokers] = useState<BrokerOption[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { tenantContext, setTenantContext, loginType } = useAuthStore();

  const currentBroker = tenantContext as BrokerContext | null;

  useEffect(() => {
    if (loginType === 'broker') {
      loadBrokers();
    }
  }, [loginType]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadBrokers = async () => {
    try {
      const data = await authService.getMyBrokers();
      setBrokers(data);
    } catch {
      // Silently handle - user might not have multiple brokers
    }
  };

  const handleSwitch = async (broker: BrokerOption) => {
    setLoading(true);
    try {
      await authService.selectBroker(broker.id);
      setTenantContext({
        id: broker.id,
        name: broker.name,
        code: broker.code,
        logo_url: broker.logo_url,
        tier: '',
        custom_domain: '',
        primary_color: '',
        secondary_color: '',
      });
      setShowDropdown(false);
      toast.success(`Switched to ${broker.name}`);
      window.location.reload();
    } catch {
      toast.error('Failed to switch broker');
    } finally {
      setLoading(false);
    }
  };

  // Don't render if not a broker user or only one broker
  if (loginType !== 'broker' || brokers.length <= 1) return null;

  return (
    <div ref={dropdownRef} className="relative">
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
      >
        <BuildingOffice2Icon className="w-4 h-4 text-gray-500" />
        <span className="max-w-[150px] truncate">{currentBroker?.name || 'Select Broker'}</span>
        <ChevronUpDownIcon className="w-4 h-4 text-gray-400" />
      </button>

      {showDropdown && (
        <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          <div className="py-1">
            {brokers.map((broker) => (
              <button
                key={broker.id}
                onClick={() => handleSwitch(broker)}
                disabled={loading || broker.id === currentBroker?.id}
                className={`w-full flex items-center gap-3 px-4 py-3 text-sm hover:bg-gray-50 transition-colors ${
                  broker.id === currentBroker?.id ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                }`}
              >
                <BuildingOffice2Icon className="w-5 h-5 flex-shrink-0" />
                <div className="flex-1 text-left min-w-0">
                  <p className="font-medium truncate">{broker.name}</p>
                  <p className="text-xs text-gray-500">{broker.role}</p>
                </div>
                {broker.is_primary && (
                  <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Primary</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
